import { useEffect, useState } from 'react';
import { api } from '../api';

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
const MOIS = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
               'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export default function LivreRecettes() {
  const now = new Date();
  const [annee, setAnnee] = useState(now.getFullYear());
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [entries, setEntries] = useState([]);
  const [totaux, setTotaux] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date_encaissement: '', reference: '', client: '', libelle: '', montant_ht: '', tva_rate: '0', montant_ttc: '', mode_paiement: 'virement' });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.getLivreRecettes(annee, mois),
      api.getLivreRecettesTotaux(annee, mois),
    ]).then(([entriesRes, totauxRes]) => {
      setEntries(entriesRes.entries || []);
      setTotaux(totauxRes);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [annee, mois]);

  const handleTvaChange = (ht, rate) => {
    const h = parseFloat(ht) || 0;
    const r = parseFloat(rate) || 0;
    return +(h * (1 + r / 100)).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.addLivreRecette(form);
      setShowForm(false);
      setForm({ date_encaissement: '', reference: '', client: '', libelle: '', montant_ht: '', tva_rate: '0', montant_ttc: '', mode_paiement: 'virement' });
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette entrée ?')) return;
    await api.deleteLivreRecette(id);
    load();
  };

  const card = (label, value, color, icon) => (
    <div style={{ flex: '1 1 200px', background: 'rgba(30,41,59,0.9)', border: `1px solid ${color}44`, borderRadius: '0.75rem', padding: '1.25rem 1.5rem' }}>
      <div style={{ fontSize: '1.4rem', marginBottom: '0.25rem' }}>{icon}</div>
      <div style={{ fontSize: '1.6rem', fontWeight: '800', color }}>{value}</div>
      <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.2rem' }}>{label}</div>
    </div>
  );

  return (
    <div style={{ color: '#e2e8f0', maxWidth: '1100px' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white', margin: 0 }}>📒 Livre de Recettes</h2>
          <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '0.3rem' }}>
            Registre obligatoire des encaissements — micro-entreprise
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.6rem 1.2rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem' }}
        >
          + Saisie manuelle
        </button>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem', alignItems: 'center' }}>
        <select
          value={annee}
          onChange={e => setAnnee(+e.target.value)}
          style={{ background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
        >
          {[now.getFullYear()-1, now.getFullYear(), now.getFullYear()+1].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select
          value={mois}
          onChange={e => setMois(+e.target.value)}
          style={{ background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', fontSize: '0.9rem' }}
        >
          {MOIS.slice(1).map((m, i) => (
            <option key={i+1} value={i+1}>{m}</option>
          ))}
        </select>
        <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{MOIS[mois]} {annee}</span>
      </div>

      {/* 3 totaux du mois */}
      {totaux && (
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          {card(`CA HT — ${MOIS[mois]}`, fmt(totaux.mois?.total_ht), '#3b82f6', '💶')}
          {card(`TVA collectée — ${MOIS[mois]}`, fmt(totaux.mois?.total_tva), '#f59e0b', '🧾')}
          {card(`CA TTC — ${MOIS[mois]}`, fmt(totaux.mois?.total_ttc), '#4ade80', '✅')}
          {card(`CA TTC annuel ${annee}`, fmt(totaux.annuel?.total_ttc), '#a78bfa', '📅')}
        </div>
      )}

      {/* Formulaire saisie manuelle */}
      {showForm && (
        <form onSubmit={handleSubmit} style={{ background: 'rgba(30,41,59,0.9)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#e2e8f0' }}>Nouvelle entrée</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            {[
              { label: 'Date encaissement *', key: 'date_encaissement', type: 'date' },
              { label: 'Référence *', key: 'reference', type: 'text', placeholder: 'FAC-001' },
              { label: 'Client *', key: 'client', type: 'text' },
              { label: 'Libellé', key: 'libelle', type: 'text' },
              { label: 'Montant HT €', key: 'montant_ht', type: 'number', step: '0.01' },
              { label: 'Taux TVA %', key: 'tva_rate', type: 'number', step: '0.1' },
              { label: 'Montant TTC € *', key: 'montant_ttc', type: 'number', step: '0.01' },
            ].map(f => (
              <div key={f.key}>
                <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '0.3rem' }}>{f.label}</label>
                <input
                  type={f.type}
                  step={f.step}
                  placeholder={f.placeholder}
                  required={f.label.endsWith('*')}
                  value={form[f.key]}
                  onChange={e => {
                    const updated = { ...form, [f.key]: e.target.value };
                    if (f.key === 'montant_ht' || f.key === 'tva_rate') {
                      updated.montant_ttc = handleTvaChange(
                        f.key === 'montant_ht' ? e.target.value : form.montant_ht,
                        f.key === 'tva_rate' ? e.target.value : form.tva_rate,
                      );
                    }
                    setForm(updated);
                  }}
                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '0.4rem', padding: '0.5rem', color: '#e2e8f0', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
            ))}
            <div>
              <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '0.3rem' }}>Mode paiement</label>
              <select
                value={form.mode_paiement}
                onChange={e => setForm({ ...form, mode_paiement: e.target.value })}
                style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '0.4rem', padding: '0.5rem', color: '#e2e8f0', fontSize: '0.9rem' }}
              >
                {['virement', 'chèque', 'espèces', 'carte', 'prélèvement'].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button type="submit" disabled={saving} style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.6rem 1.5rem', cursor: 'pointer', fontWeight: '600' }}>
              {saving ? '…' : 'Enregistrer'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} style={{ background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: '0.5rem', padding: '0.6rem 1.2rem', cursor: 'pointer' }}>
              Annuler
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ color: '#64748b', padding: '2rem', textAlign: 'center' }}>⏳ Chargement…</div>
      ) : entries.length === 0 ? (
        <div style={{ background: 'rgba(30,41,59,0.6)', borderRadius: '0.75rem', padding: '3rem', textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📭</div>
          Aucun encaissement pour {MOIS[mois]} {annee}.<br />
          <span style={{ fontSize: '0.8rem' }}>Les factures payées s'ajoutent automatiquement.</span>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
                {['Date', 'Référence', 'Client', 'Libellé', 'Montant HT', 'TVA', 'Montant TTC', 'Mode', ''].map(h => (
                  <th key={h} style={{ padding: '0.6rem 0.75rem', color: '#64748b', fontWeight: '600', fontSize: '0.75rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={e.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(30,41,59,0.3)' }}>
                  <td style={{ padding: '0.6rem 0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>{new Date(e.date_encaissement).toLocaleDateString('fr-FR')}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: '#3b82f6', fontWeight: '600' }}>{e.reference}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: '#e2e8f0', fontWeight: '600' }}>{e.client}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: '#94a3b8' }}>{e.libelle || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: '#e2e8f0', textAlign: 'right' }}>{fmt(e.montant_ht)}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: '#f59e0b', textAlign: 'right' }}>{fmt(e.tva_montant)}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: '#4ade80', fontWeight: '700', textAlign: 'right' }}>{fmt(e.montant_ttc)}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: '#64748b' }}>{e.mode_paiement}</td>
                  <td style={{ padding: '0.6rem 0.75rem' }}>
                    {!e.invoice_id && (
                      <button onClick={() => handleDelete(e.id)} style={{ background: 'transparent', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '1rem' }}>🗑</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #334155', fontWeight: '700' }}>
                <td colSpan={4} style={{ padding: '0.75rem', color: '#94a3b8', fontSize: '0.8rem', textTransform: 'uppercase' }}>Total {MOIS[mois]}</td>
                <td style={{ padding: '0.75rem', color: '#3b82f6', textAlign: 'right' }}>{fmt(totaux?.mois?.total_ht)}</td>
                <td style={{ padding: '0.75rem', color: '#f59e0b', textAlign: 'right' }}>{fmt(totaux?.mois?.total_tva)}</td>
                <td style={{ padding: '0.75rem', color: '#4ade80', textAlign: 'right' }}>{fmt(totaux?.mois?.total_ttc)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <p style={{ color: '#334155', fontSize: '0.75rem', marginTop: '1.5rem' }}>
        📌 Conformément à l'article L123-24 du Code de commerce, ce registre est obligatoire pour tout micro-entrepreneur.
        Les factures marquées comme payées sont inscrites automatiquement.
      </p>
    </div>
  );
}
